/* eslint-disable no-underscore-dangle */
import config from "../config";
import dataHelper from "./dataHelper";
import {cleanDocumentWithJTDMinMeta} from "@vostro/clean-gql/lib/jtd-min";
import type {JtdWhereFieldDef, SchemaFilterEntry} from "../types/utils";

type QueryWithCache = {
  __cache?: unknown;
  __meta?: {operations?: unknown[]};
  __jtdSchema?: unknown;
};

export function processDoc<T extends QueryWithCache>(query: T, jtdSchema: {def?: Record<string, any>}): T {
  if (!query.__cache || query.__jtdSchema !== jtdSchema) {
    try {
      const {doc, meta} = cleanDocumentWithJTDMinMeta(query as any, jtdSchema as any);
      query.__cache = doc;
      query.__meta = meta;

      if (meta?.operations?.length === 0) {
        //console.error("processDoc", query, jtdSchema, meta);
        // throw new Error("No operations found in the processed GraphQL document.");
      }

      query.__jtdSchema = jtdSchema;
    } catch (error) {
      console.error("processDoc", query, jtdSchema);
      const message = error instanceof Error ? error.message : "unknown clean-gql error";
      throw new Error(`JTD metadata is required before executing GraphQL operations: ${message}`);
    }
  }

  return query;
}

export function getGqlJdtPath() {
  return `${config.apiPath}/gqljdt.api`;
}

export function getWhereSchemaFromJtd(
  jtdSchema: {def?: Record<string, {p?: Record<string, any>; enum?: string[]}>},
  modelName: string,
) {
  const key = `GQLTQuery${dataHelper.capitalize(modelName)}Where`;
  const def = jtdSchema.def ?? {};
  const modelWhereDef = Object.prototype.hasOwnProperty.call(def, key) ? def[key] : {};
  const modelWhereDefObject = (modelWhereDef as {p?: Record<string, JtdWhereFieldDef>}).p ?? {};
  const modelWhere: Record<string, {ref?: string; whereDef?: Record<string, unknown>}> = {};

  Object.entries(modelWhereDefObject).forEach(([fieldName, value]) => {
    const typedValue = value as JtdWhereFieldDef | undefined;
    if (typedValue && Object.prototype.hasOwnProperty.call(typedValue, "ref")) {
      const ref = typedValue.ref ?? "";
      modelWhere[fieldName] = {
        ref,
        whereDef: Object.prototype.hasOwnProperty.call(def, ref) ? (def[ref] as {p?: Record<string, unknown>}).p ?? {} : {},
      };
    }
  });

  return modelWhere;
}

export function getModelFiltersFromSchema(schema: unknown) {
  const filters: Array<{name: string; columnName: string; type: string; comparitors: string[]}> = [];

  if (!schema || typeof schema !== "object") {
    return filters;
  }

  Object.entries(schema as Record<string, SchemaFilterEntry>).forEach(([key, val]) => {
    try {
      const whereDef = val.whereDef ?? {};
      if (Object.keys(whereDef).length === 0) {
        return;
      }

      const stringComparitors = Object.entries(whereDef)
        .filter(([, entry]) => Object.prototype.hasOwnProperty.call(entry, "t") && (entry as {t?: string}).t === "st")
        .map(([entryKey]) => entryKey);

      const booleanComparitors = Object.entries(whereDef)
        .filter(([, entry]) => Object.prototype.hasOwnProperty.call(entry, "t") && (entry as {t?: string}).t === "u")
        .map(([entryKey]) => entryKey);

      if (stringComparitors.length > 0) {
        filters.push({
          name: key.toUpperCase(),
          columnName: key,
          type: "string",
          comparitors: stringComparitors,
        });
      }

      if (booleanComparitors.length > 0) {
        filters.push({
          name: key.toUpperCase(),
          columnName: key,
          type: "boolean",
          comparitors: ["eq", "ne"],
        });
      }
    } catch (err) {
      console.log("schema filter err", err);
    }
  });

  return filters;
}
